import { Injectable } from '@angular/core';
import {
    getProjectOrderNumber,
    getProjectWorkOrderNumber,
    ProjectDto,
} from '@pf/platform-wms/domain';
import { asArraySafe, asNumberSafe } from '@pf/shared-utility';
import {
    ProjectPackingLineSummary,
    ProjectPackingPick,
    ProjectPackingSummary,
} from '../models/project-packing-summary.model';
import {
    PackingListFragment,
    PackingStatus,
    ProjectLineItemFragment,
} from '@control-tower/platform-wms';
import { isHandlingLtl } from '../models/cpw-equipment-types';
import {
    CpwProjectType,
    CpwProjectTypePriority,
} from '../models/cpw-project-types';
import { addDays } from 'date-fns';
import { sumBy } from 'lodash-es';

@Injectable()
export class ProjectPackingSummaryFactory {
    create(project: ProjectDto): ProjectPackingSummary {
        const lineItems = asArraySafe(project.lineItems);
        const packingLists = asArraySafe(
            project.packingLists
        ) as PackingListFragment[];
        const lines = this.getLineSummaries(lineItems);
        const pickedAt =
            lineItems
                .map(li => li.pick?.pickLocations)
                .flat()
                .map(pl => pl?.pickedDate)
                .filter(Boolean)
                .sort(
                    (a, b) =>
                        new Date(b as string).getTime() -
                        new Date(a as string).getTime()
                )[0] || project.modifiedDate;
        const hazardous = lineItems.some(li => li.item?.hazardous);
        const uniformsOnly = lineItems.every(li =>
            asArraySafe(li.item?.assignedCategories).some(
                ac => ac.code === 'uniforms'
            )
        );

        const equipmentIds = [
            ...new Set(
                lines
                    .flatMap(line => line.picks)
                    .map(pick => pick.bundle?.container?.equipment?.id)
                    .filter((id): id is string => !!id)
            ),
        ];

        return {
            id: project.id,
            isWillCall: project.projectType?.id === CpwProjectType.WillCall,
            isHotShot: project.projectType?.id === CpwProjectType.HotShot,
            warehouseId: project.warehouse.id as string,
            projectType: project.projectType!.name,
            orderNumber: getProjectOrderNumber(project),
            workOrderNumber: getProjectWorkOrderNumber(project),
            lines: lines,
            packingLists,
            equipmentIds,
            primaryLocation: project.primaryLocation ?? null,
            secondaryLocation: project.secondaryLocation ?? null,
            isLtl: lines.some(line => line.isLtl),
            isPacked:
                packingLists.length > 0 &&
                packingLists.every(pl => pl.status === PackingStatus.Packed),
            isPriority:
                !!CpwProjectTypePriority[
                    project.projectType!
                        .id as keyof typeof CpwProjectTypePriority
                ],
            pickedAt,
            dueDate:
                project.endDate ||
                addDays(
                    new Date(project.createdDate as string),
                    1
                ).toISOString(),
            isHazardous: hazardous,
            isUniformsOnly: uniformsOnly,
            requestor: project.requestingContact ?? null,
        } as ProjectPackingSummary;
    }

    private isLtl(picks: ProjectPackingPick[]): boolean {
        return picks.some(pick => {
            return isHandlingLtl(pick.bundle.container?.slot?.equipmentType);
        });
    }

    private getLineSummaries(lineItems: ProjectLineItemFragment[]) {
        return lineItems.map(li => {
            const pickLocations = asArraySafe(li.pick?.pickLocations);
            const quantityPicked = sumBy(pickLocations, pl =>
                asNumberSafe(pl.pickedQuantity)
            );
            const picks = pickLocations
                .filter(
                    pl =>
                        asNumberSafe(pl.pickedQuantity) > 0 &&
                        pl.inventoryBundle &&
                        asNumberSafe(pl.inventoryBundle.totalQuantity) > 0
                )
                .map(pl => ({
                    quantityPicked: asNumberSafe(pl.pickedQuantity),
                    bundle: pl.inventoryBundle,
                })) as ProjectPackingPick[];
            return {
                projectLineItemId: li.id,
                lineNumber: li.lineNumber as number,
                item: li.item,
                quantityRequested: asNumberSafe(li.quantity),
                quantityPicked,
                picks,
                isLtl: this.isLtl(picks),
            } as ProjectPackingLineSummary;
        });
    }
}
